/**
 * AccountExtensionsCard Component
 *
 * Card for managing account-level extensions (callouts, sitelinks, structured snippets).
 * These are CustomerAsset resources that apply across all campaigns in the account.
 *
 * Required for RMF compliance: "Callout extensions at account level"
 *
 * Data is loaded from the app_configuration (already fetched on login), not a separate API call.
 * Design matches CampaignManagement card style.
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { useAppConfigQuery } from '@/hooks/queries/useOptimizationQuery';
import { ExtensionSection } from './components/ExtensionSection';
import { PlatformBadge } from '@/components/features/CampaignCard/shared/PlatformBadge';
import type { AccountExtensionType, AccountExtensionCounts, AccountExtension } from './types';
import { ACCOUNT_EXTENSION_LABELS } from './types';
import type { AccountLevelAssetConfig } from '@/types/api';

interface AccountExtensionsCardProps {
  googleAdsAccountId: string;
  linkedAccounts?: Array<{ id: string; name: string }>;
}

// Account extension types for tabs
const EXTENSION_TYPES: AccountExtensionType[] = ['callout', 'sitelink', 'structured_snippet'];

// Convert app config asset to AccountExtension type
function mapConfigAssetToExtension(asset: AccountLevelAssetConfig): AccountExtension {
  return {
    asset_id: asset.asset_id,
    text: asset.text,
    asset_type: asset.asset_type,
    field_type: asset.field_type,
    status: asset.status,
    google_ads_account_id: asset.google_ads_account_id,
    final_url: asset.final_url,
    description1: asset.description1,
    description2: asset.description2,
    header: asset.header,
    values: asset.values,
  };
}

export function AccountExtensionsCard({
  googleAdsAccountId,
  linkedAccounts,
}: AccountExtensionsCardProps) {
  const { memberUuid } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState<AccountExtensionType>('callout');
  const [selectedAccountId, setSelectedAccountId] = useState(googleAdsAccountId);
  const headerRef = useRef<HTMLDivElement>(null);

  // Optimistic count adjustments (tracks removals per type)
  const [optimisticAdjustments, setOptimisticAdjustments] = useState<Record<AccountExtensionType, number>>({
    callout: 0,
    sitelink: 0,
    structured_snippet: 0,
  });

  // Reset optimistic adjustments when switching accounts
  React.useEffect(() => {
    setOptimisticAdjustments({
      callout: 0,
      sitelink: 0,
      structured_snippet: 0,
    });
  }, [selectedAccountId]);

  // Use app config data (already fetched on login)
  const { data: appConfig, isLoading, refetch } = useAppConfigQuery();

  // Extract account extensions from app config
  const accountExtensions = useMemo(() => {
    const extensions = appConfig?.configuration?.account_extensions;
    if (!extensions) {
      return { callouts: [], sitelinks: [], structured_snippets: [] };
    }
    return extensions;
  }, [appConfig]);

  // Get all assets as AccountExtension array
  const allAssets = useMemo((): AccountExtension[] => {
    const assets: AccountExtension[] = [];

    // Add callouts
    accountExtensions.callouts?.forEach((asset) => {
      assets.push(mapConfigAssetToExtension(asset));
    });

    // Add sitelinks
    accountExtensions.sitelinks?.forEach((asset) => {
      assets.push(mapConfigAssetToExtension(asset));
    });

    // Add structured snippets
    accountExtensions.structured_snippets?.forEach((asset) => {
      assets.push(mapConfigAssetToExtension(asset));
    });

    return assets;
  }, [accountExtensions]);

  // Get counts (scoped to selected account) with optimistic adjustments
  const counts: AccountExtensionCounts = useMemo(() => ({
    callout: Math.max(0, (accountExtensions.callouts?.filter(a => a.google_ads_account_id === selectedAccountId).length || 0) - optimisticAdjustments.callout),
    sitelink: Math.max(0, (accountExtensions.sitelinks?.filter(a => a.google_ads_account_id === selectedAccountId).length || 0) - optimisticAdjustments.sitelink),
    structured_snippet: Math.max(0, (accountExtensions.structured_snippets?.filter(a => a.google_ads_account_id === selectedAccountId).length || 0) - optimisticAdjustments.structured_snippet),
  }), [accountExtensions, selectedAccountId, optimisticAdjustments]);

  const totalExtensions = counts.callout + counts.sitelink + counts.structured_snippet;
  const hasAnyExtensions = totalExtensions > 0;

  // Format summary text for the expandable section
  const formatSummary = (): string => {
    if (!hasAnyExtensions) {
      return 'Add callouts, sitelinks, and structured snippets';
    }
    const parts: string[] = [];
    if (counts.callout > 0) parts.push(`${counts.callout} Callout${counts.callout !== 1 ? 's' : ''}`);
    if (counts.sitelink > 0) parts.push(`${counts.sitelink} Sitelink${counts.sitelink !== 1 ? 's' : ''}`);
    if (counts.structured_snippet > 0) parts.push(`${counts.structured_snippet} Snippet${counts.structured_snippet !== 1 ? 's' : ''}`);
    return parts.join(', ');
  };

  // Toggle with scroll position preservation
  const handleToggle = useCallback(() => {
    if (!headerRef.current) {
      setIsExpanded(!isExpanded);
      return;
    }

    const rect = headerRef.current.getBoundingClientRect();
    const topBefore = rect.top;

    setIsExpanded(!isExpanded);

    requestAnimationFrame(() => {
      if (headerRef.current) {
        const topAfter = headerRef.current.getBoundingClientRect().top;
        const diff = topAfter - topBefore;
        if (diff !== 0) {
          window.scrollBy(0, diff);
        }
      }
    });
  }, [isExpanded]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle();
      }
    },
    [handleToggle]
  );

  // Handle extensions updated (refresh app config)
  const handleExtensionsUpdated = useCallback(() => {
    // Reset optimistic adjustments when we refresh data from server
    setOptimisticAdjustments({
      callout: 0,
      sitelink: 0,
      structured_snippet: 0,
    });
    // Invalidate app-config to refresh data
    queryClient.invalidateQueries({ queryKey: ['app-config'] });
    refetch();
  }, [queryClient, refetch]);

  // Handle optimistic change from ExtensionSection
  const handleOptimisticChange = useCallback((removedCount: number) => {
    // Update optimistic adjustments for the currently selected type
    setOptimisticAdjustments((prev) => ({
      ...prev,
      [selectedType]: prev[selectedType] + removedCount,
    }));
  }, [selectedType]);

  // Get assets for the currently selected type and account
  const currentAssets = useMemo(() => {
    return allAssets.filter(
      (asset) =>
        asset.asset_type === selectedType &&
        asset.google_ads_account_id === selectedAccountId
    );
  }, [allAssets, selectedType, selectedAccountId]);

  return (
    <div className="bg-white rounded-lg shadow border">
      {/* Section Header - matches CampaignManagement style */}
      <div className="px-6 pt-4 pb-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <PlatformBadge platform="Google Ads" />
              <h2 className="text-lg font-semibold text-gray-900">Google Ads Account Extensions Management</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Manage account-level sitelinks, callouts, and structured snippets that apply across all campaigns.
            </p>
          </div>

          {/* Account selector (if multiple accounts) */}
          {linkedAccounts && linkedAccounts.length > 1 && (
            <div className="ml-4 flex-shrink-0">
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Select Google Ads account"
              >
                {linkedAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.id})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Expandable Extensions Section - similar to CampaignLevelCard */}
      <div className="border-b border-gray-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 focus-within:z-10 relative">
        {/* Section Header */}
        <div
          ref={headerRef}
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          className={`
            flex items-center justify-between px-4 py-3 cursor-pointer
            transition-colors focus:outline-none
            ${isExpanded ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}
          `}
          aria-expanded={isExpanded}
          aria-controls="account-extensions-content"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <ChevronDownIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              )}
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                Text Assets
              </h3>
            </div>
            <p className="mt-0.5 ml-7 text-xs text-gray-500">
              {formatSummary()}
            </p>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div id="account-extensions-content">
            {/* Tab Bar */}
            <div className="border-b border-gray-200 bg-gray-50">
              <nav className="flex -mb-px" aria-label="Account extension tabs">
                {EXTENSION_TYPES.map((type) => {
                  const isActive = selectedType === type;
                  const count = counts[type];

                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      className={`
                        px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                        ${isActive
                          ? 'border-blue-600 text-blue-600 bg-white'
                          : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }
                      `}
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`panel-${type}`}
                    >
                      {ACCOUNT_EXTENSION_LABELS[type]}
                      {count > 0 && (
                        <span
                          className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                            isActive
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Panel Content */}
            <div
              role="tabpanel"
              id={`panel-${selectedType}`}
              className="bg-white p-4"
            >
              {memberUuid ? (
                <ExtensionSection
                  memberUuid={memberUuid}
                  customerId={selectedAccountId}
                  assetType={selectedType}
                  assets={currentAssets}
                  isLoading={isLoading}
                  onAssetsUpdated={handleExtensionsUpdated}
                  onOptimisticChange={handleOptimisticChange}
                />
              ) : (
                <div className="text-center py-6 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-yellow-800">Authentication required to manage extensions.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountExtensionsCard;
